import { baseApi } from './baseApi';

export const contentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Assignment endpoints
    getAssignments: builder.query({
      query: (params = {}) => ({
        url: '/content/assignments',
        params,
      }),
      providesTags: ['Assignment'],
    }),

    getAssignment: builder.query({
      query: (id) => `/content/assignments/${id}`,
      providesTags: (result, error, id) => [{ type: 'Assignment', id }],
    }),

    createAssignment: builder.mutation({
      query: (assignmentData) => ({
        url: '/content/assignments',
        method: 'POST',
        body: assignmentData,
      }),
      invalidatesTags: ['Assignment'],
    }),

    updateAssignment: builder.mutation({
      query: ({ id, ...assignmentData }) => ({
        url: `/content/assignments/${id}`,
        method: 'PUT',
        body: assignmentData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Assignment', id },
        'Assignment',
      ],
    }),

    deleteAssignment: builder.mutation({
      query: (id) => ({
        url: `/content/assignments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Assignment'],
    }),

    // Exam endpoints
    getExams: builder.query({
      query: (params = {}) => ({
        url: '/content/exams',
        params,
      }),
      providesTags: ['Exam'],
    }),

    getExam: builder.query({
      query: (id) => `/exams/${id}`,
      providesTags: (result, error, id) => [{ type: 'Exam', id }],
    }),

    updateExam: builder.mutation({
      query: ({ id, ...examData }) => ({
        url: `/content/exams/${id}`,
        method: 'PUT',
        body: examData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Exam', id },
        'Exam',
      ],
    }),

    deleteExam: builder.mutation({
      query: (id) => ({
        url: `/content/exams/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exam'],
    }),

    // ===== QUESTION MANAGEMENT ENDPOINTS =====
    
    // Assignment Questions
    getAssignmentQuestions: builder.query({
      query: (assignmentId) => `/content/assignments/${assignmentId}/questions`,
      providesTags: (result, error, assignmentId) => [
        { type: 'AssignmentQuestion', id: assignmentId },
      ],
    }),
    
    addAssignmentQuestion: builder.mutation({
      query: ({ assignmentId, question }) => ({
        url: `/content/assignments/${assignmentId}/questions`,
        method: 'POST',
        body: question,
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: 'AssignmentQuestion', id: assignmentId },
        { type: 'Assignment', id: assignmentId },
      ],
    }),
    
    updateAssignmentQuestion: builder.mutation({
      query: ({ assignmentId, questionId, question }) => ({
        url: `/content/assignments/${assignmentId}/questions/${questionId}`,
        method: 'PUT',
        body: question,
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: 'AssignmentQuestion', id: assignmentId },
        { type: 'Assignment', id: assignmentId },
      ],
    }),
    
    deleteAssignmentQuestion: builder.mutation({
      query: ({ assignmentId, questionId }) => ({
        url: `/content/assignments/${assignmentId}/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { assignmentId }) => [
        { type: 'AssignmentQuestion', id: assignmentId },
        { type: 'Assignment', id: assignmentId },
      ],
    }),
    
    // Exam Questions
    getExamQuestions: builder.query({
      query: (examId) => `/content/exams/${examId}/questions`,
      providesTags: (result, error, examId) => [
        { type: 'ExamQuestion', id: examId },
      ],
    }),
    
    addExamQuestion: builder.mutation({
      query: ({ examId, partId, question }) => ({
        url: `/content/exams/${examId}/part/${partId}/questions`,
        method: 'POST',
        body: question,
      }),
      invalidatesTags: (result, error, { examId }) => [
        { type: 'ExamQuestion', id: examId },
        { type: 'Exam', id: examId },
      ],
    }),
    
    updateExamQuestion: builder.mutation({
      query: ({ examId, partId, questionId, question }) => ({
        url: `/content/exams/${examId}/part/${partId}/questions/${questionId}`,
        method: 'PUT',
        body: question,
      }),
      invalidatesTags: (result, error, { examId }) => [
        { type: 'ExamQuestion', id: examId },
        { type: 'Exam', id: examId },
      ],
    }),
    
    getQuestionsForPart: builder.query({
      query: ({ examId, partId }) => `/content/exams/${examId}/part/${partId}/questions`,
      providesTags: (result, error, { examId, partId }) => [
        { type: 'ExamQuestion', id: `${examId}-${partId}` },
      ],
    }),

    deleteExamQuestion: builder.mutation({
      query: ({ examId, partId, questionId }) => ({
        url: `/content/exams/${examId}/parts/${partId}/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { examId }) => [
        { type: 'ExamQuestion', id: examId },
        { type: 'Exam', id: examId },
      ],
    }),
  }),
});

export const {
  // Assignment hooks
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  
  // Exam hooks
  useGetExamsQuery,
  useGetExamQuery,
  useUpdateExamMutation,
  useDeleteExamMutation,
  
  // Question management hooks
  useGetAssignmentQuestionsQuery,
  useAddAssignmentQuestionMutation,
  useUpdateAssignmentQuestionMutation,
  useDeleteAssignmentQuestionMutation,
  useGetExamQuestionsQuery,
  useAddExamQuestionMutation,
  useUpdateExamQuestionMutation,
  useDeleteExamQuestionMutation,
  useGetQuestionsForPartQuery,
} = contentApi;
